/**
 * The one way to turn a ZodError into a response or a log line.
 *
 * Actions and routes return `toValidationError(error)`; its messages are
 * tokens (see validation-message.ts), translated where they are shown.
 * Logs use `z.prettifyError`, which keeps the path of every issue.
 */
import { z } from 'zod';

import { parseValidationMessage } from './validation-message';
import english from './validation.en.json';

// A type alias, not an interface, so it is assignable to Record<string, unknown>
// (ServiceError details and other loose bags take it as-is).
export type ValidationErrorResponse = {
  /** First form-level message, else the first field message. A token. */
  error: string;
  /** Messages per top-level field, as z.flattenError returns them. */
  fieldErrors: Record<string, string[]>;
};

export function toValidationError(error: z.ZodError): ValidationErrorResponse {
  const { formErrors, fieldErrors } = z.flattenError(error);
  const perField = fieldErrors as Record<string, string[] | undefined>;

  const cleaned: Record<string, string[]> = {};
  for (const [field, messages] of Object.entries(perField)) {
    if (messages && messages.length > 0) cleaned[field] = messages;
  }

  const first =
    formErrors[0] ?? Object.values(cleaned)[0]?.[0] ?? 'validation:invalid';

  return { error: first, fieldErrors: cleaned };
}

export function validationErrorForLog(error: z.ZodError): string {
  return z.prettifyError(error);
}

/**
 * Message for a caught error that may be a ZodError thrown by .parse(): a
 * ZodError's own message is the JSON of all its issues, so it goes through
 * toValidationError; any other Error keeps its message.
 */
export function errorMessage(error: unknown, fallback: string): string {
  if (error instanceof z.ZodError) return toValidationError(error).error;
  if (error instanceof Error) return error.message;
  return fallback;
}

type Dictionary = { [key: string]: string | Dictionary };

function lookupEnglish(key: string): string | undefined {
  let node: string | Dictionary | undefined = english as Dictionary;
  for (const part of key.replace(/^validation:/, '').split('.')) {
    if (typeof node !== 'object' || node === null) return undefined;
    node = node[part];
  }
  return typeof node === 'string' ? node : undefined;
}

/** English sentence for one message: a token is resolved, any other string is kept. */
export function englishMessage(message: string): string {
  const parsed = parseValidationMessage(message);
  if (!parsed || !parsed.key.startsWith('validation:')) return message;
  const template = lookupEnglish(parsed.key);
  if (!template) return message;
  return template.replace(/\{\{(\w+)\}\}/g, (_, name: string) =>
    String(parsed.values?.[name] ?? ''),
  );
}

/**
 * Readable English for consumers that are not the translated UI: MCP tool
 * errors an agent reads, thrown errors and logs. One `path: sentence` per issue.
 */
export function formatValidationErrorEnglish(error: z.ZodError): string {
  return error.issues
    .map((issue) => {
      const path = issue.path.join('.');
      const sentence = englishMessage(issue.message);
      return path ? `${path}: ${sentence}` : sentence;
    })
    .join('; ');
}
